/**
 * Help & Docs — in-app help center at /help.
 *
 * Three jobs:
 *  1. Onboarding hub: launch any guided tour from one place.
 *  2. Documentation: core concepts + how-tos + FAQ, written for someone
 *     who has never seen the product.
 *  3. Get help: direct line to the build team.
 *
 * Companion to the public /guide page (linkable from emails); this one lives
 * inside the app shell so tours can launch directly.
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Divider,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTour } from '../tours/TourProvider';
import { TOURS } from '../tours/tours';
import { ONBOARDING_STEP_DEFS, StepIcon } from '../onboarding/steps';
import { FEEDBACK_EMAIL, openFeedback } from '../config/feedback';
import { CONCEPTS, HOW_TOS, FAQ, HELP_GROUPS, helpSlug } from './helpContent';


// Guided-tour cards come from the shared step registry (titles are Tim's — D02).
const TOUR_CARDS = ONBOARDING_STEP_DEFS;

/**
 * P6-16: entries are grouped REPSit.ai (platform) / DREW. `openSlug` (from `/help?open=<slug>`,
 * used by the global search) expands and scrolls to one entry.
 */
function QAList({ items, openSlug }) {
  const openRef = useRef(null);
  useEffect(() => {
    if (openSlug && openRef.current) openRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [openSlug]);
  return (
    <Box>
      {items.map((item) => {
        const slug = helpSlug(item.q);
        const isOpen = openSlug && slug === openSlug;
        return (
        <Accordion key={item.q} id={`help-${slug}`} ref={isOpen ? openRef : undefined} defaultExpanded={Boolean(isOpen)} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, border: 1, borderColor: isOpen ? 'primary.main' : 'divider', borderRadius: '8px !important', mb: 1, overflow: 'hidden' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1" fontWeight={600}>{item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {(Array.isArray(item.a) ? item.a : [item.a]).map((para, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: i < (Array.isArray(item.a) ? item.a.length : 1) - 1 ? 1.25 : 0 }}>
                {para}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
        );
      })}
    </Box>
  );
}

/** One section per group (platform / DREW), skipping empty groups. */
function GroupedQA({ items, openSlug, anchors = false }) {
  return HELP_GROUPS.map((g) => {
    const list = items.filter((i) => (i.group || 'drew') === g.id);
    if (!list.length) return null;
    return (
      <Box key={g.id} id={anchors ? `help-group-${g.id}` : undefined} sx={{ mb: 3, scrollMarginTop: 80 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {g.label}
        </Typography>
        <QAList items={list} openSlug={openSlug} />
      </Box>
    );
  });
}

export default function Help() {
  const { startTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const openSlug = searchParams.get('open') || null;
  // `/help#drew` / `/help#platform` from the two sidebars' "Help & Docs" (D10, P6-16)
  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (!hash || openSlug) return;
    const el = document.getElementById(`help-group-${hash}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, openSlug]);

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Help & Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Guided tours, core concepts, how-tos, FAQs, and a direct line to the build team.
        </Typography>
      </Box>

      {/* Guided tours */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Guided Tours
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Interactive walkthroughs on the live app — each one takes you to the right page and points at the
        exact controls. About 12 minutes for all five, in order.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {TOUR_CARDS.map((t, i) => (
          <Grid item xs={12} sm={6} md={4} key={t.key}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea onClick={() => startTour(t.key)} sx={{ height: '100%', p: 0 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <StepIcon step={t} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                      {i + 1}. {t.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
                    {t.description}
                  </Typography>
                  {/* Tim 26 Oct (D03 r12, A13, A15): name the stops so "where is the Surveys walkthrough?" answers itself */}
                  {Array.isArray(TOURS[t.key]) && TOURS[t.key].length > 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                      Stops: {TOURS[t.key].map((s) => s.title).join(' · ')}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label={`~${t.minutes} min`} sx={{ height: 22 }} />
                    <Box sx={{ flexGrow: 1 }} />
                    <PlayCircleOutlineIcon color="primary" fontSize="small" />
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      Start tour
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Core concepts */}
      <Box>
        {/* Tim, Rewording Item 07 (26 Oct) */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          As REPSit evolves and additional Intelligent Assistants (IAs) are added, Your Organization, Members &amp; Roles,
          View As, Settings, the Learning Library and Document Upload Portal, Notifications, and Dashboard Customization
          will be shared with all IAs.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Help guidance is provided below for DREW (Organizational Entropy, REPS, Thresholds &amp; Warnings, OE Conditions,
          REF, Health Alerts, Surveys, and Reports). As additional IAs are released, appropriate Help guidance will be added.
        </Typography>
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Core Concepts
      </Typography>
      <GroupedQA items={CONCEPTS} openSlug={openSlug} anchors />

      <Divider sx={{ my: 4 }} />

      {/* How-tos */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        How Do I…
      </Typography>
      <GroupedQA items={HOW_TOS} openSlug={openSlug} />

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      {/* Tim 26 Oct: the onboarding packet and the User Guide as files, findable from Help */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        Downloads
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <Link href="/docs/REPSit-Beta-Onboarding-Packet.docx" download>Beta Onboarding Packet (Word)</Link>
        {' · '}
        <Link href="/docs/REPSit-Beta-User-Guide-V1.docx" download>User Guide V1 (Word)</Link>
        {' · '}
        <Link href="/guide">User Guide (in the app)</Link>
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        FAQs
      </Typography>
      <GroupedQA items={FAQ} openSlug={openSlug} />

      {/* Get help */}
      <Card sx={{ mt: 4, mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Stuck? Talk to the Humans.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2, maxWidth: 640 }}>
            You&rsquo;re one of the first to use REPSit.ai — there is no support queue, just the two
            people building this. Bugs, confusing screens, ideas, or a sandbox reset: one click away.
            (Prefer email? {FEEDBACK_EMAIL})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<EmailOutlinedIcon />}
              sx={{ color: 'primary.main', bgcolor: 'common.white', '&:hover': { bgcolor: 'grey.100' } }}
              onClick={() => openFeedback('feedback')}
            >
              Tell the Build Team
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RestartAltIcon />}
              onClick={() => openFeedback('sandbox_reset')}
            >
              Request a Sandbox Reset
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<MenuBookIcon />}
              onClick={() => window.open('/guide', '_blank', 'noopener')}
            >
              Written Getting-Started Guide
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
